import { parseFullName } from "parse-full-name";
import * as yup from "yup";
import { hasOwnProp, isEmpty, isUndefined } from "./index";
import { lgpClient } from "./LGPApiClient";
import { parsePhoneNumber, parseZipCode } from "./string";

yup.addMethod(
  yup.string,
  "address",
  function ({ errorMessage, required = true }) {
    return this.test(`test-address`, errorMessage, function (value) {
      const { path, createError } = this;
      if (isEmpty(value)) {
        if (!required) {
          return true;
        }

        return createError({ path, message: errorMessage });
      }

      const addressParts = value.split(",").map((part) => part.trim());
      if (addressParts.length !== 4) {
        return createError({
          path,
          message: "The address must have a street number and zipcode.",
        });
      }

      return true;
    });
  }
);

yup.addMethod(
  yup.string,
  "fullName",
  function ({ errorMessage, required = true }) {
    return this.test(`test-fullName`, errorMessage, function (value) {
      const { path, createError } = this;
      if (isEmpty(value)) {
        if (!required) {
          return true;
        }

        return createError({ path, message: errorMessage });
      }

      const { first, last } = parseFullName(value);
      if (isEmpty(first) || isEmpty(last)) {
        return createError({ path, message: errorMessage });
      }

      return true;
    });
  }
);

const telCache = {};

yup.addMethod(
  yup.string,
  "tel",
  function ({ errorMessage, required = true, asyncValidate }) {
    return this.test(`test-tel`, errorMessage, function (value) {
      const { path, createError } = this;
      if (isEmpty(value)) {
        if (!required) {
          return true;
        }

        return createError({ path, message: errorMessage });
      }

      const normalizedValue = parsePhoneNumber(value);
      if (isUndefined(normalizedValue)) {
        return createError({ path, message: errorMessage });
      }

      if (asyncValidate) {
        if (hasOwnProp(telCache, normalizedValue)) {
          return telCache[normalizedValue];
        } else {
          return asyncValidate(normalizedValue)
            .then(() => {
              telCache[normalizedValue] = true;
              return true;
            })
            .catch(() => {
              telCache[normalizedValue] = false;
              return createError({ path, message: errorMessage });
            });
        }
      } else {
        return true;
      }
    });
  }
);

const zipCodeCache = { 99999: true };

yup.addMethod(
  yup.string,
  "zipCode",
  function ({ errorMessage, required = true, asyncValidate }) {
    return this.test(`test-zipcode`, errorMessage, function (value) {
      const { path, createError } = this;
      if (isEmpty(value)) {
        if (!required) {
          return true;
        }

        return createError({ path, message: errorMessage });
      }

      const normalizedValue = parseZipCode(value);
      if (isUndefined(normalizedValue)) {
        return createError({ path, message: errorMessage });
      }

      if (asyncValidate) {
        if (hasOwnProp(zipCodeCache, normalizedValue)) {
          return zipCodeCache[normalizedValue];
        } else {
          return asyncValidate(normalizedValue)
            .then(() => {
              zipCodeCache[normalizedValue] = true;
              return true;
            })
            .catch(() => {
              zipCodeCache[normalizedValue] = false;
              return createError({ path, message: errorMessage });
            });
        }
      } else {
        return true;
      }
    });
  }
);

export const buildMultiStepLeadFormSchema = (fields) => {
  const shape = {};
  fields.forEach((field) => {
    let rule;

    switch (field.type) {
      case "address": {
        rule = yup.string().nullable().required(field["lf-error-message"]);
        break;
      }
      case "checkbox": {
        rule = yup.boolean().required(field["lf-error-message"]);
        break;
      }
      case "checkbox buttons": {
        rule = yup.array().min(1, field["lf-error-message"]);
        break;
      }
      case "email": {
        if (field.required) {
          rule = yup
            .string()
            .nullable()
            .required(field["lf-error-message"])
            .email(field["lf-error-message"]);
        } else {
          rule = yup.string().nullable().email(field["lf-error-message"]);
        }

        break;
      }
      case "fullname": {
        rule = yup.string().nullable().fullName({
          errorMessage: field["lf-error-message"],
          required: field.required,
        });
        break;
      }
      case "tel": {
        rule = yup.string().nullable().required(field["lf-error-message"]).tel({
          errorMessage: field["lf-error-message"],
          required: field.required,
          /* asyncValidate: (value) =>
              lgpClient.makeRequest("POST", "/validate/phoneNumber", {
                data: {
                  phoneNumber: value,
                },
              }), */
        });
        break;
      }
      case "zip": {
        rule = yup
          .string()
          .nullable()
          .required(field["lf-error-message"])
          .zipCode({
            errorMessage: field["lf-error-message"],
            required: field.required,
            asyncValidate: (value) =>
              lgpClient.makeRequest("POST", "/validate/zipCode", {
                data: {
                  zipCode: value,
                },
              }),
          });
        break;
      }
      default: {
        if (!isEmpty(field["lf-condition-field"])) {
          rule = yup
            .string()
            .nullable()
            .ensure()
            .when(field["lf-condition-field"], {
              is: field["lf-condition-value"],
              then: yup.string().required(field["lf-error-message"]),
            });
        } else if (field.required) {
          rule = yup.string().nullable().required(field["lf-error-message"]);
        }
      }
    }

    shape[field.name] = rule;
  });

  return yup.object().shape(shape);
};

export default yup;
